import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/**
 * My Performance - AMANDOUA ecranele cu numele asta.
 *
 * TABLOUL (`easeedesk-dashboards/My_performance`) e cel la care duce meniul:
 * `/easeedesk/my_performance` din `routes.php` ajunge la el, nu la metoda din
 * `Easeedesk.php`. El sta la `/app/easeedesk/my_performance`.
 *
 * RAPORTUL (`Easeedesk::my_performance` + `easeedesk-reports/My_performance`)
 * e ecranul cu coloana din stanga si Completion Time, ramas fara adresa in
 * portal. El sta la `/app/easeedesk/my_performance/completion_time`, adresa pe
 * care o are si in portal ruta raportului.
 */

export interface PerformancePage {
  title: string
  pages: { key: string; label: string }[]
}

export interface CompletionStep {
  status_name: string
  /** Secunde state in starea aia. */
  status_time: number
  /** MINUTE, ca in `it_easeedesk_ticket_status_scheme` - se inmultesc cu 60. */
  estimated_effort: number
  changed: string
}

export interface CompletionTicket {
  ID: number
  company: string
  subject: string
  category_name: string
  subcategory_name: string
  operation_name: string
  /** Secunde: minutele operatiei, inmultite cu 60 in API, ca in portal. */
  estimate_effort: number
  real_effort: number
  active_time: number
  completion_time: CompletionStep[]
}

export interface CompletionReport {
  now: string
  /** Cate coloane de "Status Completion" are capul de tabel. */
  maxStatus: number
  tickets: CompletionTicket[]
}

export const performancePageQuery = queryOptions({
  queryKey: ['easeedesk', 'my-performance', 'pages'],
  queryFn: () => apiGet<PerformancePage>('/easeedesk/my-performance/completion-time/pages'),
})

/* ---------------------------------------------------------------- TABLOUL */

export interface Named {
  id: number
  name: string
}

export interface DashboardPage {
  ready: boolean
  title: string
  departments: Named[]
}

/** Un punct din graficul "Tickets / hours". */
export interface DailyPoint {
  active: number
  progress: number
  resolved: number
  /** Ora din care s-au deschis tichetele, "HH:mm". */
  created: string
}

/** O bara din "Top Ticket Types": cate tichete vii sunt intr-o stare. */
export interface StatusCount {
  count: number
  status_name: string
  status_color: string
}

export interface DashboardData {
  ready: boolean
  active_tickets: { total: number; tookover: number; identified: number; escalated: number; planned: number }
  closed_tickets: { tookover: number; resolved: number; escalated: number; reopened: number }
  active: number
  active_daily: DailyPoint[]
  closed: number
  top: StatusCount[]
  sla: { hit: number; miss: number }
  team: {
    active: number
    closed: number
    /** `resolved` e `null` cand omul nu apare in clasament - portalul scria "n/a". */
    position: { active: number; resolved: number | null; tookover: number }
  }
}

export interface DashboardFilters {
  /** Bifa "All Active Tickets": cat e pusa, niciun filtru nu se aplica. */
  allTickets: boolean
  from: string
  to: string
  user: number
  group: number
}

export const performanceDashboardQuery = queryOptions({
  queryKey: ['easeedesk', 'my-performance', 'dashboard'],
  queryFn: () => apiGet<DashboardPage>('/easeedesk/my-performance'),
})

export function dashboardDataQuery(f: DashboardFilters) {
  const p = new URLSearchParams({ all_tickets: f.allTickets ? 'true' : 'false' })
  if (!f.allTickets) {
    p.set('start', f.from)
    p.set('end', f.to)
    if (f.user > 0) p.set('user', String(f.user))
    if (f.group > 0) p.set('group', String(f.group))
  }

  return queryOptions({
    queryKey: ['easeedesk', 'my-performance', 'dashboard-data', p.toString()],
    queryFn: () => apiGet<DashboardData>(`/easeedesk/my-performance/dashboard-data?${p.toString()}`),
  })
}

/** A doua casuta din filtre, umpluta in portal cu un POST la `user/getAdmins`. */
export function departmentUsersQuery(department: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'my-performance', 'users', department],
    queryFn: () => apiGet<{ users: Named[] }>(`/easeedesk/my-performance/users?department=${department}`),
    enabled: department > 0,
  })
}

export function completionTimeQuery(created: string, resolved: string) {
  const p = new URLSearchParams()
  if (created) p.set('created', created)
  if (resolved) p.set('resolved', resolved)
  const q = p.toString()
  return queryOptions({
    queryKey: ['easeedesk', 'completion-time', created, resolved],
    queryFn: () => apiGet<CompletionReport>(`/easeedesk/my-performance/completion-time${q ? `?${q}` : ''}`),
  })
}

export function mailCompletionReport(email: string, created: string, resolved: string) {
  return apiPost<{ ok: boolean; message?: string }>('/easeedesk/my-performance/report', {
    email,
    created,
    resolved,
  })
}
